import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { fmtDate } from '../api';
import { Badge, Card, Empty, ErrorBox, Loading, PageHeader, RiskBadge, StateBadge, Table, useApi } from '../components/ui';

interface AssetRow {
  id: string;
  requestId: string;
  channel: string;
  state: string;
  riskClass: string;
  requiredRoles: string[];
  scheduledAt: string | null;
  updatedAt: string;
  currentVersion: number;
  publishSimulated: boolean | null;
  mine?: boolean;
}

export function Approvals() {
  const { data, error, loading } = useApi<AssetRow[]>('/api/approvals');
  const [onlyMine, setOnlyMine] = useState(true);
  const rows = (data ?? []).filter((r) => !onlyMine || r.mine);
  return (
    <>
      <PageHeader title="Aprovações" subtitle="Peças aguardando decisão humana. Aprovação vale apenas para a versão e o hash exibidos." />
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Somente as que meu papel pode assinar
      </label>
      <ErrorBox error={error} />
      {loading && !data ? (
        <Loading />
      ) : rows.length ? (
        <Card>
          <Table head={['Peça', 'Canal', 'Risco', 'Exige', 'Horário previsto', 'Versão']}>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <Link className="font-mono text-brand-700 underline" to={`/conteudo/${r.id}`}>
                    {r.id}
                  </Link>
                </td>
                <td className="px-3 py-2">{r.channel}</td>
                <td className="px-3 py-2">
                  <RiskBadge risk={r.riskClass} />
                </td>
                <td className="px-3 py-2 text-xs">{r.requiredRoles.join(', ')}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.scheduledAt)}</td>
                <td className="px-3 py-2">v{r.currentVersion}</td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : (
        <Empty>Nenhuma aprovação pendente.</Empty>
      )}
    </>
  );
}

export function Studio() {
  const [state, setState] = useState('');
  const { data, error, loading } = useApi<AssetRow[]>(`/api/assets${state ? `?state=${state}` : ''}`, [state]);
  return (
    <>
      <PageHeader title="Studio Criativo" subtitle="Peças, versões e estado de produção. Abra uma peça para editar, comparar versões e acompanhar revisões." />
      <label className="mb-3 block text-sm">
        <span className="sr-only">Filtrar estado</span>
        <select className="rounded-md border border-slate-300 px-3 py-2 text-sm" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Todos os estados</option>
          {['PENDING_HUMAN_APPROVAL', 'CHANGES_REQUESTED', 'BLOCKED', 'SCHEDULED', 'PUBLISHED', 'PUBLISH_FAILED', 'EXPIRED'].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </label>
      <ErrorBox error={error} />
      {loading && !data ? (
        <Loading />
      ) : (
        <Card>
          <Table head={['Peça', 'Demanda', 'Canal', 'Estado', 'Risco', 'Versão', 'Atualizada']} empty={!data?.length}>
            {data?.map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2">
                  <Link className="font-mono text-brand-700 underline" to={`/conteudo/${r.id}`}>
                    {r.id}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.requestId}</td>
                <td className="px-3 py-2">{r.channel}</td>
                <td className="px-3 py-2">
                  <StateBadge state={r.state} /> {r.publishSimulated && <Badge tone="violet">simulado</Badge>}
                </td>
                <td className="px-3 py-2">
                  <RiskBadge risk={r.riskClass} />
                </td>
                <td className="px-3 py-2">v{r.currentVersion}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.updatedAt)}</td>
              </tr>
            ))}
          </Table>
        </Card>
      )}
    </>
  );
}

const TZ = 'America/Sao_Paulo';
const dayKey = (d: Date) => new Intl.DateTimeFormat('sv-SE', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);

export function Calendar() {
  const [view, setView] = useState<'semana' | 'mes' | 'lista'>('semana');
  const [anchor, setAnchor] = useState(() => new Date());
  const [channel, setChannel] = useState('');
  const range = useMemo(() => {
    const start = new Date(anchor);
    if (view === 'mes') {
      start.setDate(1);
      const end = new Date(start);
      end.setMonth(end.getMonth() + 1);
      return { start, end };
    }
    const wd = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - wd);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + (view === 'lista' ? 28 : 7));
    return { start, end };
  }, [anchor, view]);
  const { data, error } = useApi<AssetRow[]>(`/api/calendar?from=${range.start.toISOString()}&to=${range.end.toISOString()}`, [range.start.getTime(), range.end.getTime()]);
  const items = (data ?? []).filter((a) => !channel || a.channel === channel);
  const days: Date[] = [];
  for (let d = new Date(range.start); d < range.end; d.setDate(d.getDate() + 1)) days.push(new Date(d));
  const shift = (n: number) => {
    const d = new Date(anchor);
    if (view === 'mes') d.setMonth(d.getMonth() + n);
    else d.setDate(d.getDate() + 7 * n);
    setAnchor(d);
  };

  return (
    <>
      <PageHeader
        title="Calendário Editorial"
        subtitle="Horários em America/Sao_Paulo. Agendado ≠ publicado: a publicação só ocorre com aprovação válida no horário."
        actions={
          <>
            {(['semana', 'mes', 'lista'] as const).map((v) => (
              <button key={v} onClick={() => setView(v)} aria-pressed={view === v} className={`rounded-md px-3 py-2 text-sm ring-1 ring-slate-300 ${view === v ? 'bg-brand-700 text-white' : 'bg-white'}`}>
                {v === 'mes' ? 'mês' : v}
              </button>
            ))}
          </>
        }
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button className="rounded px-3 py-1 ring-1 ring-slate-300" onClick={() => shift(-1)} aria-label="Período anterior">
          ←
        </button>
        <span className="text-sm font-medium">
          {fmtDate(range.start, false)} – {fmtDate(new Date(range.end.getTime() - 1), false)}
        </span>
        <button className="rounded px-3 py-1 ring-1 ring-slate-300" onClick={() => shift(1)} aria-label="Próximo período">
          →
        </button>
        <label className="text-sm">
          <span className="sr-only">Canal</span>
          <select className="rounded-md border border-slate-300 px-2 py-1 text-sm" value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="">Todos os canais</option>
            <option value="linkedin_company">LinkedIn institucional</option>
            <option value="linkedin_executive">LinkedIn CEO</option>
          </select>
        </label>
      </div>
      <ErrorBox error={error} />
      {view === 'lista' ? (
        <Card>
          <Table head={['Quando', 'Peça', 'Canal', 'Estado']} empty={!items.length}>
            {items.map((a) => (
              <tr key={a.id}>
                <td className="px-3 py-2 text-xs">{fmtDate(a.scheduledAt)}</td>
                <td className="px-3 py-2">
                  <Link className="font-mono text-brand-700 underline" to={`/conteudo/${a.id}`}>
                    {a.id}
                  </Link>
                </td>
                <td className="px-3 py-2">{a.channel}</td>
                <td className="px-3 py-2">
                  <StateBadge state={a.state} />
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      ) : (
        <div className={`grid gap-2 ${view === 'mes' ? 'grid-cols-2 md:grid-cols-7' : 'grid-cols-1 md:grid-cols-7'}`}>
          {days.map((d) => {
            const key = dayKey(d);
            const today = key === dayKey(new Date());
            const list = items.filter((a) => a.scheduledAt && dayKey(new Date(a.scheduledAt)) === key);
            return (
              <div key={key} className={`min-h-24 rounded-md border bg-white p-2 ${today ? 'border-brand-500' : 'border-slate-200'}`}>
                <div className="text-xs font-semibold text-slate-600">{new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, weekday: 'short', day: '2-digit', month: '2-digit' }).format(d)}</div>
                <ul className="mt-1 space-y-1">
                  {list.map((a) => (
                    <li key={a.id}>
                      <Link to={`/conteudo/${a.id}`} className="block rounded bg-brand-50 p-1 text-[11px] hover:bg-brand-100">
                        {new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, timeStyle: 'short' }).format(new Date(a.scheduledAt!))} · {a.id}
                        <div>
                          <StateBadge state={a.state} />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
